import type { Visibility } from "@gjwtrack/domain";
import { ChevronDown, Eye } from "lucide-react";
import { client, useClient } from "../lib/client.ts";
import { ListType, Select, SelectType, type SelectItem } from "../ui/ring.ts";

const EVERYONE = "everyone";

/**
 * "Visible to issue readers" — Ring multi-select over groups and users; the first row resets to everyone.
 * Pass `issueId` for an issue (updates through the client) or `onChange` for anything else (articles, M11).
 */
export function VisibilityMenu({ issueId, visibility, editable, onChange, noun = "issue" }: { issueId?: number; visibility: Visibility | null; editable: boolean; onChange?: (v: Visibility | null) => void; noun?: string }) {
  const set = (v: Visibility | null): void => {
    if (onChange) onChange(v);
    else if (issueId !== undefined) client.updateIssue(issueId, { visibility: v });
  };
  const groups = useClient((s) => s.groups);
  const users = useClient((s) => s.schema?.data.users ?? []);
  const label = visibility === null ? `Visible to ${noun} readers` : describe(visibility, groups, users);
  const data: SelectItem[] = [
    { key: EVERYONE, label: `${noun[0]!.toUpperCase()}${noun.slice(1)} readers (everyone with access to the project)` },
    ...(groups.length ? [{ key: "t:groups", rgItemType: ListType.TITLE, label: "Groups" }] : []),
    ...groups.map((g) => ({ key: `g:${g.id}`, label: g.name })),
    { key: "t:users", rgItemType: ListType.TITLE, label: "Users" },
    ...users.map((u) => ({ key: `u:${u.id}`, label: u.name })),
  ];
  const chosen = new Set([...(visibility?.groups ?? []).map((id) => `g:${id}`), ...(visibility?.users ?? []).map((id) => `u:${id}`)]);
  const selected = data.filter((d) => chosen.has(String(d.key)));
  const apply = (items: SelectItem[]): void => {
    // Picking "everyone" (or emptying the list) drops the restriction.
    if (items.some((i) => i.key === EVERYONE)) {
      set(null);
      return;
    }
    const next: Visibility = {};
    const g = items.filter((i) => String(i.key).startsWith("g:")).map((i) => Number(String(i.key).slice(2)));
    const u = items.filter((i) => String(i.key).startsWith("u:")).map((i) => Number(String(i.key).slice(2)));
    if (g.length) next.groups = g;
    if (u.length) next.users = u;
    set(g.length || u.length ? next : null);
  };
  return (
    <Select
      multiple
      filter
      type={SelectType.CUSTOM}
      data={data}
      selected={selected}
      disabled={!editable}
      onChange={apply}
      customAnchor={({ wrapperProps, buttonProps, popup }) => (
        <span {...wrapperProps} className="relative inline-flex">
          <button
            type="button"
            id={buttonProps.id}
            disabled={buttonProps.disabled}
            onClick={buttonProps.onClick}
            data-test={buttonProps["data-test"]}
            className={`flex items-center gap-1.5 text-value ${editable ? "hover:underline" : ""}`}
          >
            <Eye size={16} />
            {label}
            <ChevronDown size={14} />
          </button>
          {popup}
        </span>
      )}
    />
  );
}

function describe(v: Visibility, groups: { id: number; name: string }[], users: { id: number; name: string }[]): string {
  const names = [
    ...(v.groups ?? []).map((id) => groups.find((g) => g.id === id)?.name ?? `group ${id}`),
    ...(v.users ?? []).map((id) => users.find((u) => u.id === id)?.name ?? `user ${id}`),
  ];
  return names.length <= 2 ? `Visible to ${names.join(", ")}` : `Visible to ${names.slice(0, 2).join(", ")} +${names.length - 2}`;
}
